import {
  OmnitrackInteractions,
  OmnitrackPauseAds,
  OmnitrackManager,
  OmnitrackPlaybackInteraction,
} from '@tvup/omnitrack-js';
import { OmnitrackPlaybackSessionParams } from '@tvup/omnitrack-js/dist/types';
import DeviceConfig from '@devices/config';
import { store } from '@shared/store/store';
import FlavourConfig from '@shared/config/flavours';
import Package from '../../package.json';
import User from '@shared/classes/user';
import AppConfig from '@shared/classes/app';
import Channel from '@shared/classes/channel';
import FastEvent from '@shared/classes/fastEvent';
import PlayerUtils from '@shared/utils/player';
import Content from '@shared/types/enums/content';
import EpgEvent from '@shared/classes/epgEvent';
import RegularEvent from '@shared/classes/regularEvent';
import StreamUtils from './stream';

/**
 * Enumeration of analytics event types that can be tracked
 */
enum AnalyticEventType {
  /** Event triggered when user restarts playback from the beginning */
  RESTART = 'StartOver',
  /** Event triggered when user pauses the ad */
  PAUSE_AD_REQUEST = 'PauseAdRequest',
  PAUSE_AD_RESPONSE = 'PauseAdResponse',
  PAUSE_AD_DISMISS = 'PauseAdDismiss',
}

/**
 * Analytics singleton class for tracking user interactions and playback sessions.
 *
 * This class manages the OmnitrackManager library instance and provides methods to:
 * - Initialize analytics with user and device information
 * - Track playback sessions (live and VOD)
 * - Update audio/subtitle tracks
 * - Track rendition changes (bitrate, level, resolution)
 * - Send analytics events (e.g., startover)
 *
 * The class uses a singleton pattern to ensure a single analytics instance
 * throughout the application lifecycle.
 */
class Analytics {
  /** Singleton instance of the Analytics class */
  private static instance: Analytics;
  /** OmnitrackManager instance for sending analytics data */
  private omnitrackManager: OmnitrackManager | null = null;
  /** Flag indicating whether analytics has been initialized */
  private initialized: boolean = false;

  /** Application name from flavour configuration */
  appName: string;
  /** Application version from package.json */
  appVersion: string;
  /** Ecosystem/platform identifier for analytics  SAMSUNG, LG...*/
  ecoSystem?: string;

  config?: {
    active?: boolean;
    frequency?: number;
    pingFrequency?: number;
    inactivitySessionTime?: number;
  };

  /**
   * Private constructor to enforce singleton pattern.
   * Initializes app name and version from configuration.
   */
  private constructor() {
    this.appVersion = Package.version;
    this.appName = FlavourConfig.appTitle;
    this.config = {
      active: false,
      frequency: 60,
      pingFrequency: 5,
      inactivitySessionTime: 1800,
    };
  }

  public setConfig(config: {
    active?: boolean;
    frequency?: number;
    pingFrequency?: number;
    inactivitySessionTime?: number;
  }) {
    this.config = config;
  }

  /**
   * Convert ISO 639-1 (two-letter) language code to ISO 639-2 (three-letter) format
   * @param langCode - ISO 639-1 language code (e.g., "es", "en", "ca")
   * @returns ISO 639-2 language code (e.g., "spa", "eng", "cat") or the original code if not found
   */
  private static translateLanguageToISO6392(langCode: string): string {
    if (!langCode || langCode.length === 0) {
      return langCode;
    }

    // If already 3 characters, assume it's already ISO 639-2
    if (langCode.length === 3) {
      return langCode.toLowerCase();
    }

    // Mapping of ISO 639-1 to ISO 639-2 codes
    const languageMap: Record<string, string> = {
      aa: 'aar', // Afar
      ab: 'abk', // Abkhazian
      ae: 'ave', // Avestan
      af: 'afr', // Afrikaans
      ak: 'aka', // Akan
      am: 'amh', // Amharic
      an: 'arg', // Aragonese
      ar: 'ara', // Arabic
      as: 'asm', // Assamese
      av: 'ava', // Avaric
      ay: 'aym', // Aymara
      az: 'aze', // Azerbaijani
      ba: 'bak', // Bashkir
      be: 'bel', // Belarusian
      bg: 'bul', // Bulgarian
      bh: 'bih', // Bihari
      bi: 'bis', // Bislama
      bm: 'bam', // Bambara
      bn: 'ben', // Bengali
      bo: 'tib', // Tibetan
      br: 'bre', // Breton
      bs: 'bos', // Bosnian
      ca: 'cat', // Catalan
      ce: 'che', // Chechen
      ch: 'cha', // Chamorro
      co: 'cos', // Corsican
      cr: 'cre', // Cree
      cs: 'cze', // Czech
      cu: 'chu', // Church Slavic
      cv: 'chv', // Chuvash
      cy: 'wel', // Welsh
      da: 'dan', // Danish
      de: 'ger', // German
      dv: 'div', // Divehi
      dz: 'dzo', // Dzongkha
      ee: 'ewe', // Ewe
      el: 'gre', // Greek
      en: 'eng', // English
      eo: 'epo', // Esperanto
      es: 'spa', // Spanish
      et: 'est', // Estonian
      eu: 'baq', // Basque
      fa: 'per', // Persian
      ff: 'ful', // Fulah
      fi: 'fin', // Finnish
      fj: 'fij', // Fijian
      fo: 'fao', // Faroese
      fr: 'fre', // French
      fy: 'fry', // Western Frisian
      ga: 'gle', // Irish
      gd: 'gla', // Scottish Gaelic
      gl: 'glg', // Galician
      gn: 'grn', // Guarani
      gu: 'guj', // Gujarati
      gv: 'glv', // Manx
      ha: 'hau', // Hausa
      he: 'heb', // Hebrew
      hi: 'hin', // Hindi
      ho: 'hmo', // Hiri Motu
      hr: 'hrv', // Croatian
      ht: 'hat', // Haitian
      hu: 'hun', // Hungarian
      hy: 'arm', // Armenian
      hz: 'her', // Herero
      ia: 'ina', // Interlingua
      id: 'ind', // Indonesian
      ie: 'ile', // Interlingue
      ig: 'ibo', // Igbo
      ii: 'iii', // Sichuan Yi
      ik: 'ipk', // Inupiaq
      io: 'ido', // Ido
      is: 'ice', // Icelandic
      it: 'ita', // Italian
      iu: 'iku', // Inuktitut
      ja: 'jpn', // Japanese
      jv: 'jav', // Javanese
      ka: 'geo', // Georgian
      kg: 'kon', // Kongo
      ki: 'kik', // Kikuyu
      kj: 'kua', // Kuanyama
      kk: 'kaz', // Kazakh
      kl: 'kal', // Kalaallisut
      km: 'khm', // Central Khmer
      kn: 'kan', // Kannada
      ko: 'kor', // Korean
      kr: 'kau', // Kanuri
      ks: 'kas', // Kashmiri
      ku: 'kur', // Kurdish
      kv: 'kom', // Komi
      kw: 'cor', // Cornish
      ky: 'kir', // Kirghiz
      la: 'lat', // Latin
      lb: 'ltz', // Luxembourgish
      lg: 'lug', // Ganda
      li: 'lim', // Limburgan
      ln: 'lin', // Lingala
      lo: 'lao', // Lao
      lt: 'lit', // Lithuanian
      lu: 'lub', // Luba-Katanga
      lv: 'lav', // Latvian
      mg: 'mlg', // Malagasy
      mh: 'mah', // Marshallese
      mi: 'mao', // Maori
      mk: 'mac', // Macedonian
      ml: 'mal', // Malayalam
      mn: 'mon', // Mongolian
      mr: 'mar', // Marathi
      ms: 'may', // Malay
      mt: 'mlt', // Maltese
      my: 'bur', // Burmese
      na: 'nau', // Nauru
      nb: 'nob', // Norwegian Bokmål
      nd: 'nde', // North Ndebele
      ne: 'nep', // Nepali
      ng: 'ndo', // Ndonga
      nl: 'dut', // Dutch
      nn: 'nno', // Norwegian Nynorsk
      no: 'nor', // Norwegian
      nr: 'nbl', // South Ndebele
      nv: 'nav', // Navajo
      ny: 'nya', // Chichewa
      oc: 'oci', // Occitan
      oj: 'oji', // Ojibwa
      om: 'orm', // Oromo
      or: 'ori', // Oriya
      os: 'oss', // Ossetian
      pa: 'pan', // Panjabi
      pi: 'pli', // Pali
      pl: 'pol', // Polish
      ps: 'pus', // Pushto
      pt: 'por', // Portuguese
      qu: 'que', // Quechua
      rm: 'roh', // Romansh
      rn: 'run', // Rundi
      ro: 'rum', // Romanian
      ru: 'rus', // Russian
      rw: 'kin', // Kinyarwanda
      sa: 'san', // Sanskrit
      sc: 'srd', // Sardinian
      sd: 'snd', // Sindhi
      se: 'sme', // Northern Sami
      sg: 'sag', // Sango
      si: 'sin', // Sinhala
      sk: 'slo', // Slovak
      sl: 'slv', // Slovenian
      sm: 'smo', // Samoan
      sn: 'sna', // Shona
      so: 'som', // Somali
      sq: 'alb', // Albanian
      sr: 'srp', // Serbian
      ss: 'ssw', // Swati
      st: 'sot', // Southern Sotho
      su: 'sun', // Sundanese
      sv: 'swe', // Swedish
      sw: 'swa', // Swahili
      ta: 'tam', // Tamil
      te: 'tel', // Telugu
      tg: 'tgk', // Tajik
      th: 'tha', // Thai
      ti: 'tir', // Tigrinya
      tk: 'tuk', // Turkmen
      tl: 'tgl', // Tagalog
      tn: 'tsn', // Tswana
      to: 'ton', // Tonga
      tr: 'tur', // Turkish
      ts: 'tso', // Tsonga
      tt: 'tat', // Tatar
      tw: 'twi', // Twi
      ty: 'tah', // Tahitian
      ug: 'uig', // Uighur
      uk: 'ukr', // Ukrainian
      ur: 'urd', // Urdu
      uz: 'uzb', // Uzbek
      ve: 'ven', // Venda
      vi: 'vie', // Vietnamese
      vo: 'vol', // Volapük
      wa: 'wln', // Walloon
      wo: 'wol', // Wolof
      xh: 'xho', // Xhosa
      yi: 'yid', // Yiddish
      yo: 'yor', // Yoruba
      za: 'zha', // Zhuang
      zh: 'chi', // Chinese
      zu: 'zul', // Zulu
      qaa: 'qaa', // Placeholder for unknown language
    };

    const normalizedCode = langCode.toLowerCase();
    return languageMap[normalizedCode];
  }

  /**
   * Get the singleton instance of Analytics.
   * Creates a new instance if one doesn't exist.
   *
   * @returns {Analytics} The singleton Analytics instance
   */
  static getInstance(): Analytics {
    if (!Analytics.instance) {
      Analytics.instance = new Analytics();
    }
    return Analytics.instance;
  }

  /**
   * Initialize OmnitrackManager with configuration.
   *
   * Sets up the analytics manager with user and device information.
   * Uses different endpoints for anonymous vs authenticated users.
   * Must be called before creating playback sessions or sending events.
   */
  initialize(userId: string | null = null): void {
    try {
      const { user } = store.getState();
      // Select endpoint based on user authentication status
      // Anonymous users use a different analytics endpoint
      const endpoint = user.isAnonymous
        ? AppConfig.getUrl('freeOmnitrack_link')
        : AppConfig.getUrl('omnitrack_link');
      if (!endpoint || !this.config?.active) {
        console.warn(
          '[Analytics] Omnitrack endpoint not configured or omnitrack not active'
        );
        return;
      }
      var customHeaders: { [key: string]: string | undefined | null } = {};
      if (user.isAnonymous) {
        customHeaders['X-Tvup-Userid'] = user.userId;
      } else {
        customHeaders['X-Access-Token'] = user.token;
      }

      // Initialize OmnitrackManager with device, user, and app information
      this.omnitrackManager = new OmnitrackManager({
        url: endpoint,
        device_id: DeviceConfig.info.uuid,
        user_id: userId || user.userId || undefined,
        app_name: this.appName.toLowerCase(),
        app_version: this.appVersion || Package.version,
        ecosystem: DeviceConfig.info.analyticsPlatform || '',
        // Get plan code from user's plan, or fallback to default plan (anonymous)
        plan: User.getPlan(user.planId)?.code || User.getPlan(null)?.code || '',
        marketmaker: user.operator?.name || '',
        connection_type: DeviceConfig.getConnectionType(),
        browser: DeviceConfig.getBrowser(),
        browser_version: DeviceConfig.getBrowserVersion(),
        device_type: DeviceConfig.getDeviceType(),
        device_model: DeviceConfig.getDeviceModel(),
        device_vendor: DeviceConfig.getDeviceVendor(),
        os: DeviceConfig.getOs(),
        os_version: DeviceConfig.getOsVersion(),
        inactivity_sesion_time: this.config?.inactivitySessionTime ?? 1800,
        analytics_frequency: this.config?.frequency ?? 60,
        ping_frequency: this.config?.pingFrequency ?? 5,
        customHeaders,
      });
      this.initialized = true;
    } catch (error) {
      console.error('[Analytics] Failed to initialize OmnitrackManager', error);
    }
  }

  /**
   * Regenerate the current analytics session ID.
   * Useful when starting a new user session or after logout/login.
   */
  updateSessionId = (): void => {
    this.omnitrackManager?.regenerateSessionId();
  };

  /**
   * Set the audio track language for the current playback session.
   * Converts the language code to ISO 639-2 format if needed.
   *
   * @param {string} track - Language code (ISO 639-1 format) e.g. "es", "en", "ca", this will be transformed to ISO 639-2 format
   */
  setAudioTrack = (track: string) => {
    const parsedTrack = Analytics.translateLanguageToISO6392(track);
    // "und" is the ISO 639-2 code for "undetermined" language
    this.omnitrackManager
      ?.getPlaybackSession()
      ?.setAudioTrack(parsedTrack || 'und');
  };

  /**
   * Set the subtitle track language and activate subtitles for the current playback session.
   * Converts the language code to ISO 639-2 format if needed.
   *
   * @param {string} track - Language code (ISO 639-1 or ISO 639-2 format)
   */
  setSubtitleTrack = (track: string) => {
    const parsedTrack = Analytics.translateLanguageToISO6392(track);
    // Activate subtitles and set the track language
    this.omnitrackManager?.getPlaybackSession()?.setSubtitleActiveState(true);
    this.omnitrackManager?.getPlaybackSession()?.setSubtitleTrack(parsedTrack);
  };

  setPlaybackInteraction = (interaction: OmnitrackPlaybackInteraction) => {
    this.omnitrackManager?.setPlaybackInteraction(interaction);
  };

  /**
   * Disable subtitles for the current playback session.
   * Clears the subtitle track and sets the active state to false.
   */
  disableSubtitles = () => {
    this.omnitrackManager?.getPlaybackSession()?.setSubtitleActiveState(false);
    this.omnitrackManager?.getPlaybackSession()?.setSubtitleTrack('');
  };

  /**
   * Send an analytics event to track user interactions.
   *
   * @param {AnalyticEventType} nameEvent - The type of event to track
   */
  addEvent = (nameEvent: AnalyticEventType): void => {
    try {
      switch (nameEvent) {
        case AnalyticEventType.RESTART:
          // Track when user restarts playback from the beginning
          this.omnitrackManager?.sendStartOverEvent(
            OmnitrackInteractions.REMOTE
          );
          break;
        case AnalyticEventType.PAUSE_AD_REQUEST:
          this.omnitrackManager?.sendPauseAdEvent(OmnitrackPauseAds.REQUEST);
          break;
        case AnalyticEventType.PAUSE_AD_RESPONSE:
          this.omnitrackManager?.sendPauseAdEvent(OmnitrackPauseAds.RESPONSE);
          break;
        case AnalyticEventType.PAUSE_AD_DISMISS:
          this.omnitrackManager?.sendPauseAdEvent(OmnitrackPauseAds.DISMISS);
          break;
      }
    } catch (error) {
      console.error('[Analytics] Failed to add event', error);
    }
  };

  /**
   * Create a playback session for tracking video playback
   * @param channel - The channel being played (for live content)
   * @param asset - The asset/event being played (for VOD content)
   */
  createPlaybackSession = (
    channel?: Channel,
    asset?: FastEvent | EpgEvent | RegularEvent
  ): void => {
    if (!this.omnitrackManager || !this.initialized) {
      console.warn(
        '[Analytics] OmnitrackManager not initialized. Call initialize() first.'
      );
      return;
    }
    const { user } = store.getState();
    try {
      // Get the event from asset parameter or current channel event
      const event = asset || channel?.currentEvent;

      // Determine if this is live or VOD
      const isLive = event?.type === Content.EPG_EVENT;
      const playbackType = PlayerUtils.getPlaybackType(event);

      // Get channel information from parameter or asset's channel
      const currentChannel = channel || asset?.channel;
      // Extract available stream formats (DASH, HLS, or free streams)
      const { dash, free, hls } =
        (event as FastEvent)?.streams || channel?.streams || {};
      // Check if user's plan is free tier
      const isFree = User.getPlan(
        User.getPlan(user.planId)?.code || User.getPlan(null)?.code || ''
      )?.isFree;
      // Determine streaming protocol: prefer free stream for free users, otherwise DASH or HLS
      const streamingProtocol = StreamUtils.getStreamingProtocol(
        (isFree && free) || dash || hls
      );

      // Build playback session parameters with all optional fields
      // These parameters are sent to Omnitrack for analytics tracking
      const playbackSessionParams: OmnitrackPlaybackSessionParams = {
        // Channel and content information
        channel_name: currentChannel?.title || '',
        channel_code: currentChannel?.code,
        program_id: event?.programId || undefined,
        event_id: event?.eventId,
        content_title: event?.title || '',
        content_series: event?.seriesTitle || undefined,
        content_season: event?.seasonNumber || undefined,
        content_episode: event?.episodeNumber || undefined,
        content_genre:
          typeof event?.genreId === 'string' ? event.genreId : undefined,
        content_language: event?.language?.toLowerCase() || '',
        // Convert start date to ISO string format if available
        content_start: event?.date?.start
          ? new Date(event.date.start).toISOString()
          : undefined,

        // Streaming information
        streaming_protocol: streamingProtocol,
        streaming_type: isLive ? 'live' : 'vod',
        playback_type: playbackType || undefined,

        // Track information (initialized as undefined/false, updated later via setter methods)
        subtitle_track: undefined,
        subtitle_active_state: false,
        audio_track: undefined,

        // Player state information, fixed values for STV and Kepler platforms
        // These values are constant for Smart TV applications
        volume_level: 100,
        mute: false,
        fullscreen_active_state: true,
        casting_active_state: false,

        // Rendition information (initialized as undefined, updated later via setter methods)
        // These track the current video quality/bitrate
        rendition_level: undefined,
        rendition_bitrate: undefined, // Should be converted to Mbps when set
        rendition_resolution: undefined,
      };

      // Create the playback session
      this.omnitrackManager.createPlaybackSession(playbackSessionParams);
    } catch (error) {
      console.error('[Analytics] Failed to create playback session', error);
    }
  };

  /**
   * Update the rendition bitrate for the current playback session.
   * Used to track quality changes during adaptive bitrate streaming.
   *
   * @param {number} bitrate - Bitrate in bps (should be converted to Mbps for Omnitrack)
   */
  setRenditionBitrate = (bitrate: number) => {
    if (typeof bitrate === 'number') {
      this.omnitrackManager?.getPlaybackSession()?.setRenditionBitrate(bitrate);
    }
  };

  /**
   * Update the rendition level for the current playback session.
   * Used to track quality level changes during adaptive bitrate streaming.
   *
   * @param {number} renditionLevel - The rendition level index
   */
  setRenditionLevel = (renditionLevel: number) => {
    if (typeof renditionLevel === 'number') {
      this.omnitrackManager
        ?.getPlaybackSession()
        ?.setRenditionLevel(renditionLevel);
    }
  };

  /**
   * Update the rendition resolution for the current playback session.
   * Used to track resolution changes during adaptive bitrate streaming.
   *
   * @param {string} resolution - Resolution string (e.g., "1920x1080", "1280x720")
   */
  setRenditionResolution = (resolution: string) => {
    if (typeof resolution === 'string') {
      this.omnitrackManager
        ?.getPlaybackSession()
        ?.setRenditionResolution(resolution);
    }
  };

  /**
   * Update content attributes for the current playback session.
   * Used when switching to a new event/content within the same session
   * (e.g., when a new program starts on a live channel).
   *
   * @param {FastEvent | EpgEvent | RegularEvent} event - The new event/content to track
   */
  setNewEvent = (event: FastEvent | EpgEvent | RegularEvent) => {
    this.omnitrackManager?.getPlaybackSession()?.setContentAttributes({
      content_title: event?.title || '',
      content_series: undefined, // TODO: Add series name, pending to discuss, it's not in the event
      content_season: event?.seasonNumber || undefined,
      content_episode: event?.episodeNumber || undefined,
      // Only set genre if it's a string (some events may have numeric genre IDs)
      content_genre:
        typeof event?.genreId === 'string' ? event.genreId : undefined,
      content_language: event?.language?.toLowerCase() || '',
    });
  };

  /**
   * Get the OmnitrackManager instance.
   * Returns null if analytics has not been initialized.
   *
   * @returns {OmnitrackManager | null} The OmnitrackManager instance or null
   */
  getOmnitrackManager(): OmnitrackManager | null {
    return this.omnitrackManager;
  }

  /**
   * Check if analytics has been initialized.
   *
   * @returns {boolean} True if analytics is initialized, false otherwise
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance - this ensures only one Analytics instance exists
const AnalyticsInstance = Analytics.getInstance();
export default AnalyticsInstance;
export { AnalyticEventType };
